import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'
import type { ReactNode } from 'react'

interface InfoTooltipProps {
  content: string
  className?: string
  tooltipClassName?: string
  children?: ReactNode
}

export const InfoTooltip = ({
  content,
  className,
  tooltipClassName,
  children,
}: InfoTooltipProps) => {
  const [isPinned, setIsPinned] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const containerRef = useRef<HTMLSpanElement>(null)

  const isVisible = isPinned || isHovered

  useEffect(() => {
    if (!isPinned) return

    const timeoutId = setTimeout(() => setIsPinned(false), 5000)

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsPinned(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isPinned])

  useEffect(() => {
    if (!isVisible || !containerRef.current) return

    const updatePosition = () => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect) return
      setPosition({ top: rect.top, left: rect.left + rect.width / 2 })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isVisible])

  return (
    <span
      ref={containerRef}
      className={`relative inline-flex gap-2 ${className ?? ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
      <button
        type="button"
        aria-label={content}
        aria-expanded={isVisible}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          setIsPinned((pinned) => !pinned)
        }}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        className="inline-flex items-center justify-center w-4 h-4 leading-none font-normal focus:outline-none focus-visible:ring-2 focus-visible:ring-fuschia"
      >
        <Info className="w-full h-full" />
      </button>
      {isVisible &&
        createPortal(
          <span
            role="tooltip"
            className={`fixed -translate-x-1/2 -translate-y-full w-max max-w-55 whitespace-normal bg-black-900 text-white text-xs font-normal rounded px-2 py-1 ${tooltipClassName ?? 'z-50'}`}
            style={{ top: position.top - 8, left: position.left }}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  )
}
